import { v4 as uuid } from "uuid"

import { SlashID } from "../main/index"

import * as api from "../clients/slashid-api"
import { createOIDCMethodOptions, createSAMLMethodOptions, OTPMethodOptions } from "../domain/options"
import * as domain from "../domain/index"
import * as internalErrors from "../domain/errors.internal"
import * as events from "../utils/events"
import { versionInfo as version } from "../version"
import { OIDCWithExtras, OrganizationID, UserTokenText, SAMLWithExtras } from "../domain/index"
import { Configuration, DefaultConfig } from "../clients/slashid-api"
import { BrowserUser } from "./user.browser"
import { ChallengeProcessor } from "../domain/challenges"
import { EventEmitter } from "../domain/emitter"
import { createRetryOnNetworkErrorMiddleware } from "../utils/fetch"
import { ERROR_NAMES } from "../domain/errors"

/**
 * This class is the main entry point for users of the SlashID SDK. It exposes a single function
 * for uniform access to all supported identification methods as {@link id}.
 * The returned {@link Types.BaseUser} object has methods to either register further
 * user handles (e.g. a second e-mail address, or a phone number), or ask the user to
 * authenticate using any number of available authentication methods. Please see {@link Types.BaseUser}
 * for further details and examples.
 *
 * For a quick start please consult the {@link constructor} and {@link id} documentation and examples.
 */
export class Flow {
    public readonly flowId: string
    private readonly slashID: SlashID
    private emitter: EventEmitter<events.NamedEvents>

    constructor(slashID: SlashID, emitter: EventEmitter<events.NamedEvents>) {
        this.slashID = slashID
        this.flowId = uuid()
        this.emitter = emitter
    }

    private shouldProcessEvent(event: events.NamedEvents): boolean {
        if (this.flowId && event.meta?.authFlowId !== this.flowId) {
            return false
        }

        return true
    }

    public async id(
        oid: OrganizationID,
        handle: api.PersonHandle,
        authenticationFactor: domain.Factor
    ): Promise<BrowserUser> {
        return this._id(oid, handle, authenticationFactor)
    }

    /**
     * @internal
     */
    public async _id(
        oid: OrganizationID,
        handle?: api.PersonHandle,
        authenticationFactor?: domain.Factor,
        prevToken?: UserTokenText
    ): Promise<BrowserUser> {
        if (authenticationFactor) {
            switch (authenticationFactor.method) {
                case api.FactorMethod.Webauthn.toString():
                    {
                        if (!(navigator && window.PublicKeyCredential)) {
                            throw new internalErrors.SlashIDError({
                                name: ERROR_NAMES.invalidAuthenticationMethodError,
                                message: "The WebAuthn API is not available in the current environment",
                                context: { method: api.FactorMethod.Webauthn, handle, authenticationFactor },
                            })
                        }

                        let opts = authenticationFactor.options as Omit<api.WebAuthnMethodOptions, "method">
                        if (!opts) {
                            opts = {}
                        }

                        // default to local scope
                        if (!opts.scope) {
                            opts.scope = window.location.hostname
                        }

                        const knownCredIds = await this.slashID.credentialStorage.get(opts.scope!!)
                        if (knownCredIds) {
                            opts.available_credential_ids = knownCredIds
                        }

                        // fallback to default SlashID WebAuthn options if not provided explicitly
                        if (!opts.attachment) {
                            opts.attachment = api.WebAuthnAuthenticatorAttachment.Any
                        }
                        if (!opts.user_verification) {
                            opts.user_verification = api.WebAuthnUserVerification.Preferred
                        }
                        if (!opts.resident_key) {
                            opts.resident_key = api.WebAuthnResidentKey.Preferred
                        }
                        if (!opts.attestation) {
                            opts.attestation = api.WebAuthnAttestation.None
                        }

                        if (opts.attachment === api.WebAuthnAuthenticatorAttachment.Platform) {
                            const hasPlatformAuthenticator =
                                await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
                            if (!hasPlatformAuthenticator) {
                                throw new internalErrors.SlashIDError({
                                    name: ERROR_NAMES.invalidAuthenticationMethodError,
                                    message: "This device has no platform authenticator available",
                                    context: { method: api.FactorMethod.Webauthn, handle, authenticationFactor },
                                })
                            }
                        }

                        authenticationFactor.options = opts
                    }
                    break
                case api.FactorMethod.EmailLink.toString():
                case api.FactorMethod.OtpViaEmail.toString():
                    if (handle && handle.type !== api.PersonHandleType.EmailAddress) {
                        throw new internalErrors.SlashIDError({
                            name: ERROR_NAMES.invalidAuthenticationMethodError,
                            message: `A handle of type '${api.PersonHandleType.EmailAddress}' is required, got '${handle.type}' instead`,
                            context: { method: api.FactorMethod.EmailLink, handle, authenticationFactor },
                        })
                    }
                    break
                case api.FactorMethod.SmsLink.toString():
                case api.FactorMethod.OtpViaSms.toString():
                    {
                        if (handle && handle.type !== api.PersonHandleType.PhoneNumber) {
                            throw new internalErrors.SlashIDError({
                                name: ERROR_NAMES.invalidAuthenticationMethodError,
                                message: `A handle of type '${api.PersonHandleType.PhoneNumber}' is required, got '${handle.type}' instead`,
                                context: { method: api.FactorMethod.SmsLink, handle, authenticationFactor },
                            })
                        }

                        authenticationFactor.options = new OTPMethodOptions(authenticationFactor.options)
                    }
                    break
                case api.FactorMethod.Oidc.toString():
                    {
                        //OIDC should not be used with handles
                        if (handle) {
                            throw new internalErrors.SlashIDError({
                                name: ERROR_NAMES.invalidAuthenticationMethodError,
                                message: "OIDC should not be used with handles",
                                context: { method: api.FactorMethod.Oidc, handle, authenticationFactor },
                            })
                        }
                        authenticationFactor = await createOIDCMethodOptions(authenticationFactor)
                        const cleanUpAfterError = (event: events.NamedEvents) => {
                            if (!this.shouldProcessEvent(event)) {
                                return
                            }

                            const oidcOptions = authenticationFactor as OIDCWithExtras
                            if (oidcOptions?.extras?.popup) {
                                oidcOptions?.extras?.popup.close()
                            }
                            this.emitter.off("oauthFlowFailed", cleanUpAfterError)
                        }

                        this.emitter.on("oauthFlowFailed", cleanUpAfterError)
                    }
                    break
                case api.FactorMethod.Saml.toString():
                    {
                        if (handle) {
                            throw new internalErrors.SlashIDError({
                                name: ERROR_NAMES.invalidAuthenticationMethodError,
                                message: "SAML should not be used with handles",
                                context: { method: api.FactorMethod.Saml, handle, authenticationFactor },
                            })
                        }
                        authenticationFactor = await createSAMLMethodOptions(authenticationFactor)

                        const cleanUpAfterError = (event: events.NamedEvents) => {
                            if (!this.shouldProcessEvent(event)) {
                                return
                            }
                            const saml = authenticationFactor as SAMLWithExtras
                            if (saml?.extras?.popup) {
                                saml?.extras?.popup.close()
                            }

                            this.emitter.off("popupFailed", cleanUpAfterError)
                        }

                        this.emitter.on("popupFailed", cleanUpAfterError)
                    }
                    break
                default:
                    break
            }
        }

        const factor = {
            method: authenticationFactor?.method,
            options: authenticationFactor?.options,
        } as api.Factor

        if (factor.method !== undefined) {
            factor.options = {
                ...factor.options,
                method: factor.method as any,
            }
        }

        this.emitter.emit({
            name: "idFlowStarted",
            payload: { authenticationFactor },
            meta: { authFlowId: this.flowId, access: "public" },
        })

        const authnClient = new api.DefaultApi(
            new Configuration({
                ...DefaultConfig,
                basePath: this.slashID.baseURL,
                middleware: [
                    createRetryOnNetworkErrorMiddleware({
                        maxRetries: 5,
                        baseDelayMs: 500,
                    }),
                ],
                ...(prevToken && { accessToken: prevToken }),
            })
        )

        const response = await authnClient.postId(
            {
                slashIDOrgID: oid,
                slashIDSdkVersion: version.raw,
                iDRequest: {
                    handle,
                    factor,
                },
            },
            { cache: "no-cache" }
        )

        if (response.errors) {
            throw response
        }

        const challenges = response.result
        if (!challenges) {
            throw new Error("no challenges in ID response")
        }

        const challengeProcessor = new ChallengeProcessor(this.slashID, this.emitter, {
            authFlowId: this.flowId,
        })
        const token = await challengeProcessor.processChallenges({
            challenges,
            authnFactor: authenticationFactor,
            previousToken: prevToken,
        })

        if (handle) {
            try {
                this.slashID.identifiersStorage.add(handle)
            } catch (e) {
                console.warn(e)
            }
        }

        /**
         *
         * @event {@link Types.IdFlowSucceededEvent}
         */
        this.emitter.emit({
            name: "idFlowSucceeded",
            payload: { token: `${token}`, authenticationFactor, handle },
            meta: { authFlowId: this.flowId, access: "public" },
        })

        return this.slashID.getUser(token!!)
    }

    /**
     * Use a verified handle and a factor to start the account recovery flow.
     * The user will receive instructions on how to proceed using a delivery mechanism based on the given handle.
     * After this method resolves, the user will be able to authenticate using the same handle and factor.
     */
    public async recover({
        handle,
        factor,
    }: {
        handle: api.PersonHandle
        factor: api.RecoverableFactor
    }): Promise<void> {
        const authnClient = new api.DefaultApi(
            new Configuration({
                ...DefaultConfig,
                basePath: this.slashID.baseURL,
                middleware: [
                    createRetryOnNetworkErrorMiddleware({
                        maxRetries: 5,
                        baseDelayMs: 500,
                    }),
                ],
            })
        )

        const { result: challenges } = await authnClient.postRecover({
            recoverRequest: {
                handle,
                factor,
            },
            slashIDSdkVersion: version.raw,
            slashIDOrgID: this.slashID.oid!,
        })

        if (!challenges) {
            // TODO improve handling
            throw new Error("no result in recover response")
        }

        const challengeProcessor = new ChallengeProcessor(this.slashID, this.emitter, {
            recoveryFlowId: this.flowId,
        })
        await challengeProcessor.processChallenges({ challenges, authnFactor: factor })
    }
}
